/**
 * ✨ KI-Moment: das Fortschritts-Fenster, während der Angebotsentwurf entsteht.
 *
 * Bis zum Arbeitspaket F1 (`docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md` Teil B2) lag dieses
 * Markup inline in `pages/anfrage-detail.tsx`. Seit die Übernahme-Aktionen auch auf der
 * Vorgangsseite (`pages/event-detail.tsx`) stehen, brauchen ZWEI Seiten dasselbe Fenster — und
 * zwei Kopien wären zwei Fassungen desselben KI-Moments, die still auseinanderlaufen.
 *
 * **Reine Extraktion, Zeichen für Zeichen dieselbe Darstellung** wie zuvor inline: `z-[70]`, KEIN
 * `backdrop-blur` (Antoine 30.07.2026: „flackert hin und her" bei zwei übereinanderliegenden
 * Unschärfe-Ebenen), dieselben drei Schritte, derselbe Balken, dieselbe Fußzeile.
 *
 * Der Katalog-Zähler wird hier selbst geladen (`GET /api/catalog/items`) statt durchgereicht:
 * er gehört zur ersten Schrittzeile dieses Fensters und zu nichts sonst auf den aufrufenden
 * Seiten. Die Komponente bleibt dafür montiert und gibt bei `open === false` schlicht `null`
 * zurück — genau der Abruf-Zeitpunkt, den die Anfrage-Detailseite vorher selbst hatte.
 */
import { useApi } from "../../hooks/useApi";
import { Button, Icon } from "../components";

export interface AngebotErzeugungModalProps {
  /** Läuft die Erzeugung gerade? (`generating` aus `useAngebotUebernahme`) */
  open: boolean;
  /** 0–3, s. `useAngebotUebernahme` (Phase der Anfrage bzw. 3 nach dem Erfolg). */
  genStep: number;
  /** Gästezahl des Falls — erscheint als Detail an der zweiten Schrittzeile. */
  guests: number | null;
  /** „Abbrechen" in der Fußzeile (`cancelGenerate` aus `useAngebotUebernahme`). */
  onAbbrechen: () => void;
}

export function AngebotErzeugungModal({ open, genStep, guests, onAbbrechen }: AngebotErzeugungModalProps) {
  const { data: catalogItems } = useApi<{ id: string }[]>("/api/catalog/items");
  if (!open) return null;

  const steps: { label: string; detail: string }[] = [
    { label: "Katalog & Pakete geladen", detail: catalogItems ? `Katalog · ${catalogItems.length} Positionen` : "" },
    { label: "KI erstellt den Entwurf", detail: guests ? `${guests} Gäste` : "" },
    { label: "Entwurf wird gespeichert", detail: "" },
  ];

  return (
    <div className="fixed inset-x-0 top-0 z-[70] flex h-dvh items-center justify-center bg-on-surface/45 p-[30px]">
      <div className="w-full max-w-[560px] overflow-hidden rounded-2xl bg-surface-container-lowest shadow-xl dark:bg-surface-dim">
        <div className="flex flex-col items-center gap-[13px] px-[30px] pb-1 pt-7 text-center">
          <div className="grid h-[54px] w-[54px] place-items-center rounded-2xl bg-primary-container text-primary dark:bg-primary/15">
            <Icon name="auto_awesome" className="text-2xl" />
          </div>
          <h3 className="font-headline-sm text-[19px] font-bold tracking-tight text-on-surface dark:text-inverse-on-surface">Ihr Angebot entsteht</h3>
          <p className="max-w-[400px] font-body-sm text-[12.5px] leading-relaxed text-secondary dark:text-secondary-fixed-dim">
            Die KI liest die Anfrage und stellt aus Katalog &amp; Paketen einen Entwurf zusammen.
          </p>
        </div>

        <ul className="px-[30px] pt-[14px]">
          {steps.map((step, i) => {
            const done = i < genStep;
            const active = i === genStep;
            return (
              <li
                key={step.label}
                className={`grid grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-[11px] py-2.5 ${
                  i < steps.length - 1 ? "border-b border-outline-variant/60 dark:border-outline/40" : ""
                }`}
              >
                {done ? (
                  <span className="grid h-[22px] w-[22px] place-items-center rounded-full bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                    <Icon name="check" className="text-[13px]" />
                  </span>
                ) : active ? (
                  <span className="grid h-[22px] w-[22px] place-items-center rounded-full border-2 border-primary bg-primary-container/50 text-primary">
                    <Icon name="progress_activity" className="animate-spin text-[13px]" />
                  </span>
                ) : (
                  <span className="h-[22px] w-[22px] rounded-full border-[1.5px] border-dashed border-outline-variant" />
                )}
                <span
                  className={`text-[12.5px] ${
                    active ? "font-semibold text-primary" : done ? "font-semibold text-on-surface dark:text-inverse-on-surface" : "font-medium text-secondary/70 dark:text-secondary-fixed-dim/70"
                  }`}
                >
                  {step.label}
                </span>
                <span className="text-right text-[11px] text-on-surface-variant/70">{step.detail}</span>
              </li>
            );
          })}
        </ul>

        <div className="mx-[30px] mt-3 h-[6px] overflow-hidden rounded-full bg-surface-container-high dark:bg-white/[0.08]">
          <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${((genStep + 1) / steps.length) * 100}%` }} />
        </div>

        <div className="flex items-center justify-between gap-3 px-[30px] pb-[22px] pt-[14px]">
          <p className="text-[11px] text-on-surface-variant/70">Gesendet wird erst nach Ihrer Freigabe — der Entwurf öffnet sich im Builder.</p>
          <Button type="button" variant="secondary" className="shrink-0" onClick={onAbbrechen}>
            Abbrechen
          </Button>
        </div>
      </div>
    </div>
  );
}
